// Payload of the "set-element" downlink event. Cast the Event in the handler
// (the overload below takes a plain Event so EventTarget subclasses like
// PlaygroundClient — whose addEventListener is the DOM one — still satisfy it).
export type SetElementEvent = CustomEvent<{ element: string }>;
// Payload of the "set-mode" downlink event (interaction mode: 'line' | 'pad').
export type SetModeEvent = CustomEvent<{ mode: string }>;
// Payload of the "set-uv-hz" downlink event (FINGER_UV send rate, 1–60 Hz).
export type SetUvHzEvent = CustomEvent<{ uvHz: number }>;
// Payload of the "set-max-fingers" downlink event (notes one seat may hold, 1–10).
export type SetMaxFingersEvent = CustomEvent<{ maxFingers: number }>;
// Payload of the "set-uv-pack" downlink event (positions per packet, 1–4).
export type SetUvPackEvent = CustomEvent<{ uvPack: number }>;
// Payload of the "set-show" downlink event (0 before the show | 1 the show | 2 after).
export type SetShowEvent = CustomEvent<{ show: number }>;

export interface CsClient {
  // u,v are the finger's position at attack (0..1). Optional so mock/playground
  // clients that only need the line/finger can omit them; the real WS client
  // forwards them so the projection colours the ring by true wavelength.
  sendNoteOn(line: number, finger: number, u?: number, v?: number): void;
  sendNoteOff(line: number, finger: number): void;
  // `tick` hands the frame to the packer instead of the socket; omit it and the
  // position goes out on its own, now. Returns false when the client recognised
  // the position as one already on the wire and sent nothing — the mock and
  // playground clients return nothing at all, which reads as "sent".
  sendFingerUV(finger: number, u: number, v: number, tick?: number): boolean | void;
  // Send whatever the packer is holding as one packet. Optional: only the real
  // WS client packs anything.
  flushUvPack?(tick?: number): void;
  readonly connected: boolean;
  // Latest server-assigned element symbol (null until the first SET_ELEMENT
  // downlink arrives). Optional so the mock / playground clients need not set it.
  readonly element?: string | null;
  // Latest server-assigned interaction mode (null until the first SET_MODE
  // downlink arrives). Optional so mock / playground clients need not set it.
  readonly mode?: string | null;
  // Latest server-assigned FINGER_UV send rate in Hz (null until SET_UV_HZ).
  readonly uvHz?: number | null;
  // Latest server-assigned per-seat finger cap (null until SET_MAX_FINGERS).
  // The server refuses notes past this on its own, but silently — the phone
  // needs the number so it stops handing out slots it cannot sound.
  readonly maxFingers?: number | null;
  // Latest server-assigned positions-per-packet (null until SET_UV_PACK).
  readonly uvPack?: number | null;
  // Which screen this phone should be on (null until the server has said).
  readonly show?: number | null;
  // True once the server has refused this seat. Read it as well as listening
  // for "seat-busy": the socket connects at import time, so the event usually
  // fires before anything is wired up to hear it.
  readonly seatBusy?: boolean;
  // True once an operator has removed this phone. Same reason as seatBusy for
  // being readable as a property: the refusal usually lands before the UI is up.
  readonly removed?: boolean;
  // True once this phone has opened the show in another tab and that tab has
  // taken it. Read as a property more urgently than either of the two above:
  // the newcomer decides the eviction, so this socket is closed a millisecond
  // or two after the other one opens — reliably before this UI exists.
  readonly anotherTab?: boolean;
  // True once an operator has sent this phone back to the door. Read as a
  // property for the same reason as the three above.
  readonly sentBack?: boolean;
  // Try the seat again after "seat-busy". Optional: the mock and playground
  // clients have no seat to be refused.
  retrySeat?(): void;
  addEventListener(type: "connecting" | "connected" | "disconnected", cb: () => void): void;
  // Fired once when the server refuses this seat because another phone is
  // already playing it. Nothing reconnects after this: the seat is taken, and
  // retrySeat() is the only way back in.
  addEventListener(type: "seat-busy", cb: () => void): void;
  // Fired once when an operator removes this phone. Nothing reconnects after
  // this and there is no retry: the block is on this device and on the code it
  // arrived with, so a retry could only be refused again.
  addEventListener(type: "removed", cb: () => void): void;
  // Fired once when the same phone opens the show in another tab. Nothing
  // reconnects after this, and that is the whole point of it having a code of
  // its own: two tabs that each redial on being closed would spend the show
  // evicting one another. Taking the show back is a deliberate press.
  addEventListener(type: "another-tab", cb: () => void): void;
  // Fired once when an operator sends this phone back to the door: closed and
  // freed, nothing blocked. Nothing reconnects; the page leaves for the scanner.
  addEventListener(type: "sent-back", cb: () => void): void;
  addEventListener(type: "set-element", cb: (ev: Event) => void): void;
  addEventListener(type: "set-mode", cb: (ev: Event) => void): void;
  addEventListener(type: "set-uv-hz", cb: (ev: Event) => void): void;
  addEventListener(type: "set-max-fingers", cb: (ev: Event) => void): void;
  addEventListener(type: "set-uv-pack", cb: (ev: Event) => void): void;
  // Which screen the phone should be showing. Sent on every open, so a phone
  // that arrives mid-interval is told before it draws anything.
  addEventListener(type: "set-show", cb: (ev: Event) => void): void;
}

interface WsClientModule {
  csClient: CsClient;
}

let cached: CsClient | null = null;

export async function getCsClient(): Promise<CsClient> {
  if (cached) return cached;
  // ws-client.js lives in client/js — Vite resolves it relative to src/net.
  const mod = (await import("../../js/ws-client.js")) as unknown as WsClientModule;
  cached = mod.csClient;
  return cached;
}
